import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { initiatePurchase } from '../../services/creditService';
import { CreditPackage } from '../../types/credits';
import { CreditCard, Check, Package, ShieldCheck } from 'lucide-react';

interface CreditPurchaseFormProps {
  onSuccess?: () => void;
}

const CreditPurchaseForm: React.FC<CreditPurchaseFormProps> = ({ onSuccess }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<CreditPackage>({
    id: 'single',
    priceId: 'price_1Qg6O7HgJ40en0UjimZLQyII',
    credits: 1,
    price: 49.90,
    discount: 0
  });

  // Définition des packages disponibles
  const creditPackages: CreditPackage[] = [
    { id: 'single', priceId: 'price_1Qg6O7HgJ40en0UjimZLQyII', credits: 1, price: 49.90, discount: 0 },
    { id: 'double', priceId: 'price_1RLeyHHgJ40en0UjKzgKieav', credits: 2, price: 94.90, discount: 5 },
    { id: 'medium', priceId: 'price_1RLezQHgJ40en0UjeL1pj3CZ', credits: 5, price: 224.90, discount: 10 },
    { id: 'large', priceId: 'price_1RLezwHgJ40en0UjxJuu0HE5', credits: 10, price: 399.90, discount: 20 }
  ];

  // Calcul du prix original avant remise
  const calculateOriginalPrice = (credits: number, unitPrice: number = 49.90) => {
    return credits * unitPrice;
  };

  // Calcul de l'économie réalisée
  const calculateSavings = (pack: CreditPackage) => {
    const originalPrice = calculateOriginalPrice(pack.credits);
    return originalPrice - pack.price;
  };

  const handlePurchase = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('Initialisation de l\'achat:', {
        userId: user.uid,
        credits: selectedPackage.credits,
        price: selectedPackage.price,
        priceId: selectedPackage.priceId
      });
      
      const result = await initiatePurchase(
        user.uid, 
        selectedPackage.credits, 
        selectedPackage.price,
        selectedPackage.priceId
      );
      
      const { url } = result;
      console.log('URL de redirection Stripe:', url);
      
      if (!url) {
        throw new Error('Aucune URL de redirection n\'a été générée');
      }
      
      // Redirection directe vers Stripe Checkout
      window.location.href = url;
    } catch (err) {
      console.error('Erreur lors de l\'initialisation du paiement:', err);
      setError('Une erreur est survenue lors de l\'initialisation du paiement. Veuillez réessayer.');
      setLoading(false);
    }
  };

  return (
    <>
      <div className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {creditPackages.map((pack) => (
            <div 
              key={pack.id}
              onClick={() => setSelectedPackage(pack)}
              className={`
                bg-white rounded-lg p-6 border-2 cursor-pointer transition-all
                ${selectedPackage.id === pack.id 
                  ? 'border-primary shadow-lg transform scale-105' 
                  : 'border-gray-200 hover:border-primary/30'}
              `}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center">
                  {selectedPackage.id === pack.id ? (
                    <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center mr-2">
                      <Check className="h-3 w-3" />
                    </div>
                  ) : (
                    <div className="w-6 h-6 rounded-full border-2 border-gray-300 mr-2"></div>
                  )}
                  <Package className={`h-5 w-5 ${selectedPackage.id === pack.id ? 'text-primary' : 'text-gray-500'}`} />
                </div>
                {pack.discount > 0 && (
                  <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                    -{pack.discount}%
                  </span>
                )}
              </div>
              
              <h3 className={`text-xl font-semibold ${selectedPackage.id === pack.id ? 'text-primary' : 'text-gray-700'}`}>
                {pack.credits} {pack.credits > 1 ? 'crédits' : 'crédit'}
              </h3>
              
              <div className="mt-2">
                <p className="text-2xl font-bold text-primary">{pack.price.toFixed(2)} €</p>
                
                {pack.discount > 0 && (
                  <div className="flex items-center mt-1">
                    <p className="text-sm text-gray-500 line-through mr-2">
                      {calculateOriginalPrice(pack.credits).toFixed(2)} €
                    </p>
                    <p className="text-sm text-green-600">
                      Économie: {calculateSavings(pack).toFixed(2)} €
                    </p>
                  </div>
                )}
                
                <p className="text-sm text-gray-500 mt-1">
                  {(pack.price / pack.credits).toFixed(2)} € par crédit
                </p>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={handlePurchase}
          disabled={loading}
          className="w-full btn btn-primary py-3 text-lg flex items-center justify-center"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
              Redirection en cours...
            </>
          ) : (
            <>
              <CreditCard className="h-5 w-5 mr-2" />
              Acheter {selectedPackage.credits > 1 ? 'ces crédits' : 'ce crédit'}
            </>
          )}
        </button>

        {error && (
          <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            {error}
          </div>
        )}
      </div>

      <div className="space-y-4 text-sm text-neutral-600">
        <div className="flex items-start space-x-2">
          <div className="flex-shrink-0 mt-1">
            <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <p className="flex items-center">
              <span>Paiement sécurisé par Stripe</span>
              <ShieldCheck className="h-4 w-4 ml-1 text-green-500" />
            </p>
            <p className="text-xs text-gray-500 mt-1">Toutes les transactions sont cryptées et sécurisées</p>
          </div>
        </div>
        <div className="flex items-start space-x-2">
          <div className="flex-shrink-0 mt-1">
            <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <p>Crédit valable à vie</p>
            <p className="text-xs text-gray-500 mt-1">Pas d'expiration, utilisez vos crédits quand vous voulez</p>
          </div>
        </div>
        <div className="flex items-start space-x-2">
          <div className="flex-shrink-0 mt-1">
            <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <p>Estimations détaillées illimitées pour une même adresse</p>
            <p className="text-xs text-gray-500 mt-1">Modifiez et comparez autant de versions que vous voulez</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreditPurchaseForm;